
import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Link, Shapeable, ContentNode as TContentNode } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { applyOpenGraphThumbnailSize, classNames } from '@shapeable/utils';
import { CellLabel, ContentEntityIntro, ContentEntityRichTitle, ContentNode, GptButton, useLang, useModal } from '@shapeable/ui';
import { ExplorerHomeLinkCards } from './explorer-home-link-cards';
import { Page } from '@shapeable/gesda-types';
import { AiLowerCaseIconGlyph, ChevronCorneredRightIconGlyph } from '@shapeable/icons';
import { GptButtonSummary } from '../../data';
import { AiChatInterfaceModal } from '../elements/ai-chat-interface-modal';
import { GESDA_GREEN, GESDA_PURPLE_LIGHT } from '../../theme';
import { get } from 'lodash';
const cls = classNames('explorer-home-view-layout');

// -------- Types -------->

// -------- Props -------->

export type ExplorerHomeViewLayoutProps = Classable & HasChildren & { 
  entity?: Page;
};

export const ExplorerHomeViewLayoutDefaultProps: Omit<ExplorerHomeViewLayoutProps, 'entity'> = {
};

// -------- Child Component Props -------->

type ContainerProps = {

};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(4)};
    padding-top: ${theme.UNIT(4)};  
  `,
});

const ContentStyles = breakpoints({
  base: css`
    
  `,
});

const SectionStyles = breakpoints({
  base: css`
    margin-bottom: ${theme.UNIT(5)};
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(3)};
  `,
});

const TitleAndIntroStyles = breakpoints({
  base: css`
  `,
});

const IntroStyles = breakpoints({
  base: css`
    color: rgba(0, 0, 0, 0.67);
    font-size: 16px;
    font-style: normal;
    font-weight: 450;
  `,
});

const TitleStyles = breakpoints({
  base: css`
    font-weight: 300;
    margin-bottom: ${theme.UNIT(7)};
    line-height: 1.10;
    font-size: 1.8em;
  `,
});

const ContentLabelStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('text')};
    font-weight: 400;
    margin-bottom: ${theme.UNIT(1)};
  `,
});

const GptButtonStyles = breakpoints({
  base: css`
    .shp--gpt-button__gpt-pill {
      font-weight: 400;
      font-family: ${theme.FONT('sans')};
      font-size: ${theme.FONT_SIZE(14)};
      background: linear-gradient(90deg, ${GESDA_PURPLE_LIGHT}, ${GESDA_GREEN});
    }
  `,
});

const ButtonAndContentStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.UNIT(4)};
  `,
  landscape: css`
    flex-direction: row;
    align-items: center;
  `,
});

const GptButtonSummaryStyles = breakpoints({
  base: css`
    line-height: 1.35em;
    font-weight: 300;
    font-size: 0.9em;
  `,
});

// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,

    TitleAndIntro: styled.div`${TitleAndIntroStyles}`,
      Title: styled(ContentEntityRichTitle)`${TitleStyles}`,
      Intro: styled(ContentEntityIntro)`${IntroStyles}`,

    Content: styled.div`${ContentStyles}`,
      Section: styled.section`${SectionStyles}`,
        ContentLabel: styled(CellLabel)`${ContentLabelStyles}`,
        ButtonAndContent: styled.div`${ButtonAndContentStyles}`,
          GptButton: styled(GptButton)`${GptButtonStyles}`,
          GptButtonSummary: styled(ContentNode)`${GptButtonSummaryStyles}`,
};

export const ExplorerHomeViewLayout: Shapeable.FC<ExplorerHomeViewLayoutProps> = (props) => {
  const { className, children, entity } = props;

  const { cardLinks = [] } = entity;

  const applyCardThumbnails = applyOpenGraphThumbnailSize('card');

  const prepareCardLink = (cardLink: Link) => {
    const topicOpenGraph = get(cardLink, 'topic.openGraph');
    const topicPath = get(cardLink, 'topic.path');
    return applyCardThumbnails({
      ...cardLink,
      ...(topicOpenGraph && { openGraph: topicOpenGraph }),
      ...(topicPath && { path: topicPath }),
    });
  };
  const featurePagesTrimmed = cardLinks.slice(0, 4).map(prepareCardLink);

  const hasLinkCards = !!featurePagesTrimmed.length;
 
  const t = useLang();
  const modal = useModal();

  const handleChatClick = () => {
    modal?.show(<AiChatInterfaceModal />, { color: '#000000', title: 'RadarAI', opacity: 0.5 });
  };
  
  return (
    <My.Container className={cls.name(className)}>
      <My.TitleAndIntro>
        <My.Intro entity={entity} />
      </My.TitleAndIntro>
      {
        hasLinkCards &&
        <ExplorerHomeLinkCards
          items={featurePagesTrimmed}
          spacing={4}
          minColumns={1}
          tabletAutoColumns={4}
          desktopAutoColumns={4}
        />
      }
      <My.Content>
        <My.Section>
          <My.ContentLabel>{t('Generate personalised results with:')}</My.ContentLabel>
          <My.ButtonAndContent>
            <My.GptButton 
              showHoverBorderAnimation
              onClick={handleChatClick} 
              rightIconComponent={ChevronCorneredRightIconGlyph}
              entity={entity}
              label='Use RadarAI' 
              colors={{ 
                text: 'canvas', 
                fill: 'canvas',
              }} 
              icon={AiLowerCaseIconGlyph} 
              />
            <My.GptButtonSummary entity={GptButtonSummary} />
          </My.ButtonAndContent>
        </My.Section>
      </My.Content>
    </My.Container>
  )
};

ExplorerHomeViewLayout.defaultProps = ExplorerHomeViewLayoutDefaultProps;
ExplorerHomeViewLayout.cls = cls;